import React, { useEffect, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';

/**
 * JobTypeToggle is a React component that renders a toggle button group to select job types.
 * It allows users to filter jobs based on their type, such as full-time or part-time.
 * The component uses the Material-UI ToggleButton and ToggleButtonGroup components for styling.
 * 
 * @param {Array<string>} jobTypes - An array of available job types to initialize the toggle state.
 * @param {function} handleFetchJobFromJobType - Callback function to fetch jobs based on the selected job type.
 * @param {boolean} resetToggle - A flag to reset the toggle state to the initial job type.
 * 
 * @returns {React.ReactElement} A JSX element representing the job type toggle buttons.
 */

const JobTypeToggle = ({ alignment, handleFetchJobFromJobType }) => {

  /**
   * Handles the change event when a toggle button is clicked.
   * Updates the alignment state with the new job type and calls the
   * handleFetchJobFromJobType callback function to fetch jobs based on the
   * selected job type.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the toggle button group.
   * @param {string} newAlignment - The new job type selected from the toggle button group.
   */
  const handleToggleChange = (event, newAlignment) => {
    if (newAlignment === null) return;

    handleFetchJobFromJobType(newAlignment);
  };

  return (
    <Box className="job-type-toggle-wrapper">
      <ToggleButtonGroup
        className="job-type-toggle"
        color="primary"
        value={alignment}
        exclusive
        onChange={handleToggleChange}
        aria-label="Platform"
        sx={{ gap: '10px' }}
      >
        <ToggleButton value="both" disableRipple>All Jobs</ToggleButton>
        <ToggleButton value="Full time" disableRipple>Full Time Jobs</ToggleButton>
        <ToggleButton value="Part time" disableRipple>Part Time / Temp Jobs</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default JobTypeToggle;
